import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { FirebaseError } from "firebase/app";
import { sendPasswordResetEmail, signInWithEmailAndPassword } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../lib/firebase.ts";
import { isUsername, toLoginEmail } from "../lib/username.ts";
import { useSession } from "../lib/session.tsx";
import { SiteFooter } from "../App.tsx";

export default function Login({
  blockedReason,
}: {
  blockedReason?: "noAccount" | "suspended" | "wrongCampaign";
}) {
  const { t, locale, setLocale, logout, firebaseUser } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetFailed, setResetFailed] = useState(false);
  const [bootstrapName, setBootstrapName] = useState("");
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  /**
   * Claims the first super_admin. The callable refuses as soon as any user
   * record exists, so this button is harmless to leave in place — after
   * setup it simply reports that an administrator already exists.
   */
  async function claimFirstAdmin() {
    setBusy(true);
    setBootstrapError(null);
    try {
      await httpsCallable(
        functions,
        "bootstrapFirstAdmin",
      )({
        displayName: bootstrapName.trim(),
        locale,
      });
      // The users/{uid} listener in SessionProvider picks this up and the app
      // renders itself; no reload needed.
    } catch (err) {
      setBootstrapError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setLoginFailed(false);
    try {
      await signInWithEmailAndPassword(auth, toLoginEmail(email), password);
    } catch {
      // Deliberately generic: distinguishing "wrong password" from "no such
      // user" would let anyone probe for valid staff addresses.
      setLoginFailed(true);
    } finally {
      setBusy(false);
    }
  }

  async function enterDemo() {
    setBusy(true);
    setLoginFailed(false);
    try {
      await signInWithEmailAndPassword(auth, "demo@epdistrict8.com", "CanalesDemo2026!");
    } catch {
      setLoginFailed(true);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Firebase failures that say nothing about whether an account exists. These
   * are our own misconfiguration or the network, so hiding them behind the
   * generic "if the email belongs to an account" message only hides it from
   * us: a continue URL on a domain missing from Firebase's authorized list
   * sends no mail at all, and the screen used to claim it had.
   */
  const CONFIG_FAILURES = new Set([
    "auth/unauthorized-continue-uri",
    "auth/invalid-continue-uri",
    "auth/missing-continue-uri",
    "auth/operation-not-allowed",
    "auth/unauthorized-domain",
    "auth/network-request-failed",
    "auth/internal-error",
    "auth/too-many-requests",
  ]);

  async function resetPassword() {
    // Username accounts have no mailbox; their resets go through an administrator.
    if (!email.trim() || isUsername(email)) return;
    setBusy(true);
    setResetSent(false);
    setResetFailed(false);
    try {
      await sendPasswordResetEmail(auth, toLoginEmail(email), {
        url: `${window.location.origin}/login`,
      });
      setResetSent(true);
    } catch (error) {
      // Whether an account exists stays secret — that is what stops this
      // control enumerating staff. A failure that cannot reveal that is
      // reported, because silence here is indistinguishable from success.
      if (error instanceof FirebaseError && CONFIG_FAILURES.has(error.code)) setResetFailed(true);
      else setResetSent(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <Link className="landing-login-back" to="/">
          ← {t("auth.platformOverview")}
        </Link>
        <div className="auth-brand">{t("app.name")}</div>
        <div className="auth-tagline">{t("app.tagline")}</div>

        {blockedReason ? (
          <>
            <div
              className={
                blockedReason === "suspended" ? "callout callout-danger" : "callout callout-warn"
              }
            >
              {t(
                blockedReason === "suspended"
                  ? "auth.suspended"
                  : blockedReason === "wrongCampaign"
                    ? "auth.wrongCampaign"
                    : "auth.noAccount",
              )}
            </div>
            {/* First-run path: the very first person in needs a way to claim
                super_admin, since there is no administrator yet to provision
                them. Only offered on the no-account screen. */}
            {blockedReason === "noAccount" && (
              <div
                style={{
                  marginBottom: 16,
                  paddingBottom: 16,
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <h4 style={{ marginBottom: 4 }}>
                  {locale === "es"
                    ? "¿Primera vez que se configura?"
                    : "Setting up for the first time?"}
                </h4>
                <p className="faint small" style={{ marginBottom: 10 }}>
                  {locale === "es"
                    ? "Si aún no existe ningún usuario, puedes reclamar la cuenta de administrador. Esto deja de estar disponible en cuanto exista el primer usuario."
                    : "If no user exists yet, you can claim the administrator account. This stops being available as soon as the first user exists."}
                </p>

                {bootstrapError && (
                  <div className="callout callout-danger small">{bootstrapError}</div>
                )}

                <div className="field">
                  <label htmlFor="bootstrapName">{t("admin.name")}</label>
                  <input
                    id="bootstrapName"
                    type="text"
                    value={bootstrapName}
                    onChange={(e) => setBootstrapName(e.target.value)}
                    placeholder={firebaseUser?.email ?? ""}
                  />
                </div>

                <button
                  type="button"
                  className="primary"
                  disabled={busy || bootstrapName.trim().length < 2}
                  onClick={() => void claimFirstAdmin()}
                  style={{ width: "100%" }}
                >
                  {busy ? (
                    <>
                      <span className="spinner" /> {locale === "es" ? "Reclamando…" : "Claiming…"}
                    </>
                  ) : locale === "es" ? (
                    "Reclamar administrador"
                  ) : (
                    "Claim administrator"
                  )}
                </button>
              </div>
            )}

            <button type="button" onClick={() => void logout()}>
              {t("nav.signOut")}
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <h1 style={{ fontSize: "1.05rem", marginBottom: 4 }}>{t("auth.title")}</h1>
            <p className="faint small" style={{ marginBottom: 18 }}>
              {t("auth.subtitle")}
            </p>

            {loginFailed && <div className="callout callout-danger">{t("auth.failed")}</div>}

            <div className="field">
              <label htmlFor="email">{t("auth.emailOrUsername")}</label>
              <input
                id="email"
                type="text"
                autoCapitalize="none"
                spellCheck={false}
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="password">{t("auth.password")}</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <button
              type="submit"
              className="primary"
              disabled={busy}
              style={{ width: "100%", marginTop: 6 }}
            >
              {busy ? t("auth.working") : t("auth.submit")}
            </button>
            {/* The only recovery path for someone locked out days from the
                election. It is a real secondary button, not grey small print. */}
            <button
              type="button"
              disabled={busy || !email.trim() || isUsername(email)}
              onClick={() => void resetPassword()}
              style={{ width: "100%", marginTop: 8 }}
            >
              {t("auth.resetPassword")}
            </button>
            {isUsername(email) && (
              <div className="faint small" style={{ marginTop: 8 }}>
                {t("auth.resetEmailOnly")}
              </div>
            )}
            {resetSent && (
              <div className="callout callout-info small" style={{ marginTop: 10 }}>
                {t("auth.resetSent")}
              </div>
            )}
            {resetFailed && (
              <div className="callout callout-warn small" style={{ marginTop: 10 }}>
                {t("auth.resetFailed")}
              </div>
            )}
          </form>
        )}

        {!blockedReason && (
          <div className="demo-login-card">
            <div>
              <span className="demo-login-kicker">{t("auth.demoKicker")}</span>
              <strong>{t("auth.demoTitle")}</strong>
              <p>{t("auth.demoBody")}</p>
            </div>
            <button type="button" onClick={() => void enterDemo()} disabled={busy}>
              {busy ? t("auth.demoOpening") : t("auth.demoLaunch")}
            </button>
          </div>
        )}

        <div
          className="lang-toggle"
          style={{ marginTop: 20 }}
          role="group"
          aria-label={t("nav.language")}
        >
          <button type="button" aria-pressed={locale === "es"} onClick={() => setLocale("es")}>
            Español
          </button>
          <button type="button" aria-pressed={locale === "en"} onClick={() => setLocale("en")}>
            English
          </button>
        </div>

        <SiteFooter />
      </div>
    </div>
  );
}
